import React, { useState, useRef, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {Search, X, AlertCircle, User, CheckCircle} from 'lucide-react'
import { lumi } from "../lib/lumi"
import toast from "react-hot-toast"

interface User {
  _id: string
  userId: string
  email: string
  fullName?: string
  role: string
  status?: string
}

export default function SuperadminProfileSwitcher() {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [searchType, setSearchType] = useState<"name" | "id">("name")
  const [results, setResults] = useState<User[]>([])
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(false)
  const [includeInactive, setIncludeInactive] = useState(false)
  const modalRef = useRef<HTMLDivElement>(null)
  const searchTimeout = useRef<NodeJS.Timeout>()
  const navigate = useNavigate()

  // Debounced search
  useEffect(() => {
    if (searchTerm.length < 2) {
      setResults([])
      return
    }

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current)
    }

    searchTimeout.current = setTimeout(() => {
      handleSearch()
    }, 300)

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current)
      }
    }
  }, [searchTerm, searchType, includeInactive])

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isOpen])

  const handleSearch = async () => {
    setLoading(true)
    try {
      const filter: any = {}

      if (searchType === "name") {
        filter.fullName = { $regex: searchTerm, $options: "i" }
      } else {
        filter.userId = { $regex: searchTerm, $options: "i" }
      }

      // Filter by status
      if (!includeInactive) {
        filter.status = { $ne: "blocked" }
      }

      const result = await lumi.entities.UserProfiles.list({
        filter,
        limit: 10,
        sort: { _id: -1 }
      })

      setResults(result.list)
    } catch (error) {
      console.error("Search error:", error)
      toast.error("Search failed")
    } finally {
      setLoading(false)
    }
  }

  const handleSelectUser = (user: User) => {
    setSelectedUser(user)
  }

  const handleEnterProfile = async () => {
    if (!selectedUser) return

    const superadmin = lumi.auth.user
    if (!superadmin) {
      toast.error("Not authenticated")
      return
    }

    try {
      // Generate session token
      const sessionToken = `imp_${Date.now()}_${Math.random().toString(36).substring(7)}`
      const now = new Date().toISOString()

      // Create impersonation session record
      await lumi.entities.ImpersonationSessions.create({
        session_token: sessionToken,
        superadmin_id: superadmin.userId,
        superadmin_email: superadmin.email,
        viewed_user_id: selectedUser.userId,
        viewed_user_email: selectedUser.email,
        viewed_role: selectedUser.role,
        entered_at: now,
        status: "active",
        ip_address: "",
        user_agent: navigator.userAgent,
        actions_logged: []
      })

      // Create audit log
      try {
        await lumi.entities.AuditLogs.create({
          actor_id: superadmin.userId,
          action: "impersonation_started",
          target_id: selectedUser.userId,
          timestamp: now,
          metadata: {
            actor_email: superadmin.email,
            target_email: selectedUser.email,
            target_name: selectedUser.fullName || "",
            viewed_role: selectedUser.role,
            session_token: sessionToken
          }
        })
      } catch (auditError) {
        console.warn("Audit log creation failed (non-blocking):", auditError)
      }

      // Store session in sessionStorage
      sessionStorage.setItem("impersonation_session", JSON.stringify({
        sessionToken,
        superadminId: superadmin.userId,
        superadminEmail: superadmin.email,
        viewedUserId: selectedUser.userId,
        viewedUserEmail: selectedUser.email,
        viewedUserName: selectedUser.fullName || selectedUser.email,
        viewedRole: selectedUser.role,
        enteredAt: now
      }))

      toast.success(`Viewing ${selectedUser.fullName || selectedUser.email}'s ${selectedUser.role} dashboard`)

      // Navigate to role-specific dashboard
      const roleRoutes: Record<string, string> = {
        nurse: "/dashboard/nurse",
        recruiter: "/dashboard/recruiter",
        affiliate: "/dashboard/affiliate",
        admin: "/dashboard/admin",
        admin_regional: "/dashboard/admin"
      }

      navigate(roleRoutes[selectedUser.role] || "/dashboard/nurse")
      setIsOpen(false)
    } catch (error) {
      console.error("Enter profile error:", error)
      toast.error("Failed to enter profile")
    }
  }

  const getRoleBadgeColor = (role: string) => {
    const colors: Record<string, string> = {
      superadmin: "bg-amber-100 text-amber-800",
      admin: "bg-purple-100 text-purple-800",
      admin_regional: "bg-indigo-100 text-indigo-800",
      recruiter: "bg-blue-100 text-blue-800",
      affiliate: "bg-green-100 text-green-800",
      nurse: "bg-teal-100 text-teal-800"
    }
    return colors[role] || "bg-gray-100 text-gray-800"
  }

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 px-4 py-2 bg-white text-amber-900 rounded-lg hover:bg-amber-50 transition-colors font-medium border border-amber-200"
        title="Profile Switcher (Impersonation)"
      >
        <User className="w-4 h-4" />
        <span className="hidden sm:inline">View As</span>
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div ref={modalRef} className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white p-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">Profile Switcher</h2>
                <p className="text-sm text-amber-100 mt-1">Impersonate users to test their experience</p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* Warning Banner */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900">
                  <p className="font-semibold mb-1">Security Notice</p>
                  <p>All actions while viewing are audited and tagged with your superadmin ID. PII access is logged.</p>
                </div>
              </div>

              {!selectedUser ? (
                <>
                  {/* Search Controls */}
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSearchType("name")}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                          searchType === "name"
                            ? "bg-amber-600 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        Search by Name
                      </button>
                      <button
                        onClick={() => setSearchType("id")}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                          searchType === "id"
                            ? "bg-amber-600 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        Search by ID
                      </button>
                    </div>

                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={`Enter ${searchType === "name" ? "user name" : "user ID"}...`}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        autoFocus
                      />
                    </div>

                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeInactive}
                        onChange={(e) => setIncludeInactive(e.target.checked)}
                        className="w-4 h-4 text-amber-600 rounded focus:ring-2 focus:ring-amber-500"
                      />
                      <span>Include inactive/blocked users</span>
                      {includeInactive && (
                        <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">⚠️ Caution</span>
                      )}
                    </label>
                  </div>

                  {/* Search Results */}
                  {loading && (
                    <div className="text-center py-8 text-gray-500">
                      <div className="animate-spin w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                      <p>Searching...</p>
                    </div>
                  )}

                  {!loading && searchTerm.length >= 2 && results.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <User className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                      <p>No users found</p>
                    </div>
                  )}

                  {!loading && results.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-gray-700">Results ({results.length})</p>
                      {results.map((user) => (
                        <button
                          key={user._id}
                          onClick={() => handleSelectUser(user)}
                          className="w-full p-4 border border-gray-200 rounded-lg hover:border-amber-500 hover:bg-amber-50 transition-colors text-left"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-gray-900">
                                {user.fullName || "No name"} <span className="text-gray-500 text-sm">({user.userId})</span>
                              </p>
                              <p className="text-sm text-gray-600">{user.email}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                                {user.role}
                              </span>
                              {user.status === "blocked" && (
                                <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium">
                                  Blocked
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Selected User Panel */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-bold text-gray-900">Selected User</h3>
                      <button
                        onClick={() => setSelectedUser(null)}
                        className="text-sm text-amber-600 hover:text-amber-700 font-medium"
                      >
                        ← Back to search
                      </button>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                      <div>
                        <p className="text-sm text-gray-600">Name</p>
                        <p className="font-semibold text-gray-900">{selectedUser.fullName || "No name"}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Email</p>
                        <p className="font-semibold text-gray-900">{selectedUser.email}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">User ID</p>
                        <p className="font-mono text-sm text-gray-900">{selectedUser.userId}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Role</p>
                        <span className={`inline-block px-3 py-1 rounded text-sm font-medium ${getRoleBadgeColor(selectedUser.role)}`}>
                          {selectedUser.role}
                        </span>
                      </div>
                      {selectedUser.status === "blocked" && (
                        <div className="bg-red-50 border border-red-200 rounded p-3">
                          <p className="text-sm text-red-900 font-semibold">⚠️ This account is blocked</p>
                        </div>
                      )}
                    </div>

                    {/* Confirmation */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-900 font-semibold mb-2">
                        <CheckCircle className="w-4 h-4 inline mr-1" />
                        Confirm Impersonation
                      </p>
                      <p className="text-sm text-blue-800">
                        You will enter viewing mode for <strong>{selectedUser.fullName || selectedUser.email}</strong> as <strong>{selectedUser.role}</strong>.
                        All actions will be audited and tagged with your superadmin ID.
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3">
                      <button
                        onClick={handleEnterProfile}
                        className="flex-1 bg-amber-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-amber-700 transition-colors"
                      >
                        Enter Profile
                      </button>
                      <button
                        onClick={() => setSelectedUser(null)}
                        className="px-6 py-3 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
